"use client";

import * as React from "react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ConfirmationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: React.ReactNode;
    onConfirm: () => void;
    /** Fires on Cancel button click only. Not fired on overlay click or Escape. */
    onCancel?: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: "default" | "destructive";
    isPending?: boolean;
    closeOnConfirm?: boolean;
}

function ConfirmationDialog({
    open,
    onOpenChange,
    title,
    description,
    onConfirm,
    onCancel,
    confirmLabel,
    cancelLabel,
    variant = "default",
    isPending = false,
    closeOnConfirm = true,
}: ConfirmationDialogProps) {
    const resolvedConfirmLabel =
        confirmLabel ?? (variant === "destructive" ? "Delete" : "Confirm");
    const resolvedCancelLabel = cancelLabel ?? "Cancel";

    const handleConfirm = () => {
        if (isPending) return;
        onConfirm();
        if (closeOnConfirm) onOpenChange(false);
    };

    const handleCancel = () => {
        if (isPending) return;
        onCancel?.();
        onOpenChange(false);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && isPending) return;
        onOpenChange(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-slot="confirmation-dialog"
                showCloseButton={false}
            >
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isPending}
                    >
                        {resolvedCancelLabel}
                    </Button>
                    <Button
                        variant={
                            variant === "destructive"
                                ? "destructive"
                                : "default"
                        }
                        onClick={handleConfirm}
                        disabled={isPending}
                    >
                        {resolvedConfirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { ConfirmationDialog };
export type { ConfirmationDialogProps };
